import React, { useEffect } from 'react';
import { Checkbox, Drawer, Form, Input, InputNumber, Select, Button, Row, Col } from 'antd';
import { SaveExternalStationInput } from '../graphql/mutations/externalStationMutations';

const STATION_TYPE_OPTIONS = [
  { label: 'TEMU_GA', value: 1 },
  { label: 'TEMU_SCF', value: 2 },
  { label: 'TEMU_HANDOVER', value: 3 },
  { label: 'TIKTOK', value: 4 },
];

type ExternalStationFormDrawerProps = {
  visible: boolean;
  onClose: () => void;
  onSubmit: (values: SaveExternalStationInput) => Promise<void> | void;
  initialValues?: SaveExternalStationInput;
  mode: 'create' | 'edit';
};

const ExternalStationFormDrawer: React.FC<ExternalStationFormDrawerProps> = ({
  visible,
  onClose,
  onSubmit,
  initialValues,
  mode,
}) => {
  const [form] = Form.useForm<SaveExternalStationInput>();

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (initialValues) {
        form.setFieldsValue(initialValues);
      }
    }
  }, [visible, initialValues, form]);

  const handleFinish = async (values: SaveExternalStationInput) => {
    await onSubmit({ ...values, id: initialValues?.id });
  };

  return (
    <Drawer
      open={visible}
      width={560}
      onClose={onClose}
      title={mode === 'create' ? 'New External Station' : 'Edit External Station'}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()}>Save</Button>
        </div>
      }
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please enter station name' }]}
        >
          <Input placeholder="Enter station name" />
        </Form.Item>

        <Form.Item
          label="Sorting Code"
          name="sortingCode"
          rules={[{ required: true, message: 'Please enter sorting code' }]}
        >
          <Input placeholder="Enter sorting code" />
        </Form.Item>

        <Form.Item label="Carrier Code" name="carrierCode">
          <InputNumber placeholder="Enter carrier code (optional)" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Address Name"
          name="addressName"
          rules={[{ required: true, message: 'Please enter address name' }]}
        >
          <Input placeholder="Enter address name" />
        </Form.Item>

        <Form.Item label="Station Type" name="stationType">
          <Select
            options={STATION_TYPE_OPTIONS}
            allowClear
            placeholder="Select station type"
          />
        </Form.Item>

        <Form.Item
          label="Address Line 1"
          name="addressLine1"
          rules={[{ required: true, message: 'Please enter address line 1' }]}
        >
          <Input placeholder="Enter address line 1" />
        </Form.Item>

        <Form.Item label="Address Line 2" name="addressLine2">
          <Input placeholder="Enter address line 2 (optional)" />
        </Form.Item>

        <Form.Item
          label="Timezone Location"
          name="timezoneLocation"
          rules={[{ required: true, message: 'Please enter timezone location' }]}
        >
          <Input placeholder="e.g. America/New_York" />
        </Form.Item>

        <Form.Item label="Contact Name" name="contactName">
          <Input placeholder="Enter contact name (optional)" />
        </Form.Item>

        <Form.Item label="Phone Number" name="phoneNumber">
          <Input placeholder="Enter phone number (optional)" />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="Country" name="country">
              <Input placeholder="e.g. US" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="State" name="state">
              <Input placeholder="e.g. CA" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="City" name="city">
              <Input placeholder="Enter city" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Zipcode" name="zipcode">
              <Input placeholder="Enter zipcode" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Temu Warehouse ID"
          name="temuWarehouseId"
          tooltip="调用 Temu 箱贴/封箱等接口时作为 warehouseId 传入，如 SWIF.LAX.01；慎重修改"
        >
          <Input placeholder="Enter Temu warehouse ID (optional)" />
        </Form.Item>

        {/* 测试站点只能与测试仓组成 TMS 路线，与正式站点互相隔离 */}
        <Form.Item name="isTest" valuePropName="checked" initialValue={false}>
          <Checkbox>Test Station</Checkbox>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default ExternalStationFormDrawer;
